"use client";

/**
 * Client-side auth glue for the candidate session.
 *
 * v2 is same-origin with the legacy Assessment-React app, so whatever token the
 * candidate's session holds is readable here and forwarded to our own BFF as a
 * Bearer header (the BFF proxies it to the real service). The legacy app stores
 * it in TWO places depending on how the candidate arrived:
 *   1. `localStorage.token` — set after OTP verification / token refresh.
 *   2. redux-persist (`localStorage["persist:root"]` → `auth` slice → `token`) —
 *      set on sign-in. Reading only (1) misses this case and the session looks
 *      empty, so read both.
 */
function fromPersistRoot(): string | null {
  try {
    const root = localStorage.getItem("persist:root");
    if (!root) return null;
    const slices = JSON.parse(root) as Record<string, string>;
    if (!slices.auth) return null;
    const auth = JSON.parse(slices.auth) as { token?: string };
    return auth?.token ?? null;
  } catch {
    return null;
  }
}

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem("token") || fromPersistRoot();
  } catch {
    return fromPersistRoot();
  }
}

/** Persist the token minted by the OTP sign-in, where v1 also looks for it. */
export function setToken(token: string): void {
  try {
    localStorage.setItem("token", token);
  } catch {}
}

/** Authorization header for BFF fetches (empty when unauthenticated). */
export function authHeaders(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Wipe the local session everywhere the token can live. */
export function clearSession(): void {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("persist:root");
    sessionStorage.clear();
  } catch {}
}
